import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const ROLE_LABELS = {
  admin: "Administrator",
  manager: "Manager",
  store_keeper: "Store Incharge",
  qc: "QC Inspector",
  worker: "Worker",
  guard: "Security Guard",
  sup_material: "Material Allotment Supervisor",
  sup_manufacturing: "Manufacturing Supervisor",
  sup_polish: "Sanding & Polishing Supervisor",
  sup_hardware: "Final Hardware Supervisor",
  sup_packing: "Packing & Labeling Supervisor",
  sup_stuffing: "Stuffing Supervisor",
};

export const rolesOf = (user) => (user?.roles?.length ? user.roles : (user?.role ? [user.role] : []));
export const hasRole = (user, ...roles) => rolesOf(user).some((r) => roles.includes(r));

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(() => {
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem("aranya_token");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // CRITICAL: returning from OAuth callback — skip /me, AuthCallback exchanges the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("aranya_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processSession = async (sessionId) => {
    const { data } = await api.post("/auth/session", { session_id: sessionId });
    if (data.token) localStorage.setItem("aranya_token", data.token);
    setUser(data);
    setLoading(false);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Server logout failed; clearing local session", e);
    }
    localStorage.removeItem("aranya_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, processSession, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
